import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import clsx from "clsx";
import {
  makeStyles,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Box,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  AppBar,
  Toolbar,
  Slide,
  CardActions,
  TableContainer,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import {
  WatchLater,
  Save,
  Add,
  Edit,
  Delete,
  GroupAdd,
  MonetizationOn,
  AccountBalance,
} from "@material-ui/icons";
import { selectConjunto } from "src/redux/slices/complexSlice";
import { StyledButton } from "src/components/CustomButtons/TgButton";
import { NoData, StyledCell, StyledRow } from "src/components/Common";
import {
  addAuthorizedResident,
  deleteAuthorizedResident,
  getAuthorizedResidents,
  updateAuthorizedResident,
} from "src/views/viewGuardia/parking/services/firestore";
import { suscriberBankAccounts } from "src/services/admin/accounts";
import { getParkey, updateParkey } from "../services/firestore";
import NewBank from "./NewBank";
import EditBank from "./EditBank";
import DeleteBank from "./DeleteBank";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
}));

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const ConfigParkey = () => {
  const classes = useStyles();
  const conjunto = useSelector(selectConjunto);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [authorizedResidents, setAuthorizedResidents] = useState([]);
  const [editingResident, setEditingResident] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [parkeyPay, setParkeyPay] = useState([]);
  const [residentToDelete, setResidentToDelete] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState({});

  const columns = [
    { id: "nombre", label: "Nombre" },
    { id: "apellido", label: "Apellido" },
    { id: "cedula", label: "Cédula" },
    { id: "placa", label: "Placa" },
    { id: "color", label: "Color de Auto" },
    { id: "acciones", label: "Acciones" },
  ];

  const columnsBanks = [
    { id: "nroCuenta", label: "Nro Cuenta" },
    { id: "banco", label: "Banco" },
    { id: "tipo", label: "Tipo de Cuenta" },
    { id: "identificacion", label: "CI / RUC" },
    { id: "acciones", label: "Acciones" },
  ];

  useEffect(() => {
    setLoading(true);
    getParkey(
      {
        next: (res) => {
          const items = res.data();
          setParkeyPay(items);
          setLoading(false);
        },
        error: () => {
          enqueueSnackbar(
            "Error al obtener datos, ¡Inténtelo de nuevo más tarde!",
            { variant: "error" }
          );
        },
      },
      conjunto.id
    );
  }, [conjunto.id]);

  useEffect(() => {
    const unsub = suscriberBankAccounts(conjunto.id, {
      next: (res) => {
        const data = res.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setCuentas(data);
        setLoading(false);
      },
      error: () => {
        setLoading(false);
        enqueueSnackbar("¡Error al obtener información de las cuentas!", {
          variant: "error",
        });
      },
    });
    return unsub;
  }, [conjunto.id]);

  useEffect(() => {
    const fetchAuthorizedResidents = async () => {
      try {
        const residents = await getAuthorizedResidents(conjunto.id);
        setAuthorizedResidents(residents);
      } catch (error) {
        enqueueSnackbar(
          "Error al obtener residentes autorizados. Intente nuevamente",
          { variant: "error" }
        );
      }
    };
    fetchAuthorizedResidents();
  }, [conjunto.id, enqueueSnackbar]);

  const handleFormSubmit = async (values, { resetForm, setSubmitting }) => {
    const data = { ...values };
    try {
      if (editingResident) {
        await updateAuthorizedResident(conjunto.id, editingResident.id, data);
        setAuthorizedResidents((prev) =>
          prev.map((res) =>
            res.id === editingResident.id ? { ...res, ...data } : res
          )
        );
        enqueueSnackbar("Residente autorizado actualizado exitosamente", {
          variant: "success",
        });
      } else {
        const newResident = await addAuthorizedResident(conjunto.id, data);
        setAuthorizedResidents((prev) => [
          ...prev,
          { id: newResident.id, ...data },
        ]);
        enqueueSnackbar("Residente autorizado agregado exitosamente", {
          variant: "success",
        });
      }
      resetForm();
    } catch (error) {
      enqueueSnackbar(
        "Error al guardar residente autorizado. Intente nuevamente",
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
      setEditingResident(null);
      handleCloseCreate();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setLoadingDelete(true);
      await deleteAuthorizedResident(conjunto.id, residentToDelete.id);
      setAuthorizedResidents((prev) =>
        prev.filter((r) => r.id !== residentToDelete.id)
      );
      setLoadingDelete(false);

      enqueueSnackbar("Residente autorizado eliminado exitosamente", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        "Error al eliminar residente autorizado. Intente nuevamente",
        { variant: "error" }
      );
    } finally {
      handleCloseConfirmDelete();
    }
  };

  const saveBankAccount = async () => {
    try {
      const data = { CuentaId: selectedCuenta.id };
      await updateParkey(conjunto.id, data);
      enqueueSnackbar("Cuenta bancaria actualizada exitosamente", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Error al actualizar la cuenta bancaria", {
        variant: "error",
      });
    }
  };

  const handleEditClick = (resident) => {
    setEditingResident(resident);
    setOpenCreate(true);
  };

  const handleCreate = () => {
    setOpenCreate(true);
  };

  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
    setResidentToDelete(null);
  };

  const handleDeleteClick = (resident) => {
    setResidentToDelete(resident);
    setOpenConfirmDelete(true);
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
    setEditingResident(null);
  };

  return (
    <div>
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={{
              ValorFraccion: parkeyPay?.ValorFraccion || 0,
              TiempoGratis: parkeyPay?.TiempoGratis || 0,
              ConsumoMinimo: parkeyPay?.ConsumoMinimo || 0,
              HorasGratis: parkeyPay?.HorasGratis || 0,
            }}
            validationSchema={Yup.object().shape({
              ValorFraccion: Yup.number().min(0),
              TiempoGratis: Yup.number().max(24).min(0),
              ConsumoMinimo: Yup.number().min(0),
              HorasGratis: Yup.number().max(24).min(0),
            })}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const data = {
                  ValorFraccion: values.ValorFraccion * 1,
                  TiempoGratis: values.TiempoGratis * 1,
                  ConsumoMinimo: values.ConsumoMinimo * 1,
                  HorasGratis: values.HorasGratis * 1,
                };
                await updateParkey(conjunto.id, data);
                setParkeyPay((prev) => ({
                  ...prev,
                  ...data,
                }));
                enqueueSnackbar("Costo modificado exitosamente.", {
                  variant: "success",
                });
                setSubmitting(false);
              } catch (error) {
                enqueueSnackbar("Error al actualizar los datos.", {
                  variant: "error",
                });
                setSubmitting(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              touched,
              values,
            }) => (
              <Form className={clsx(classes.root)}>
                <Box mt={3}>
                  <Card>
                    <CardHeader
                      title={
                        <span style={{ fontSize: "0.9rem" }}>
                          <WatchLater fontSize="small" /> Precio del parqueadero
                          por hora
                        </span>
                      }
                      style={{ paddingBottom: 9, paddingTop: 9 }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="ValorFraccion"
                            label="Valor por fracción (*incluído IVA):"
                            name="ValorFraccion"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={values.ValorFraccion}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            helperText={
                              errors.ValorFraccion &&
                              touched.ValorFraccion &&
                              errors.ValorFraccion
                            }
                            error={
                              errors.ValorFraccion && touched.ValorFraccion
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="TiempoGratis"
                            label="Tiempo inicial gratis:"
                            name="TiempoGratis"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={values.TiempoGratis}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.TiempoGratis && touched.TiempoGratis}
                            helperText={
                              errors.TiempoGratis &&
                              touched.TiempoGratis &&
                              errors.TiempoGratis
                            }
                          />
                        </Grid>
                        <Grid container item xs={12} spacing={3} />
                      </Grid>
                    </CardContent>
                  </Card>
                  <Divider style={{ marginBottom: "16px", width: "100%" }} />
                  <Card>
                    <CardHeader
                      title={
                        <span style={{ fontSize: "0.9rem" }}>
                          <MonetizationOn fontSize="small" /> Descuento por
                          consumos
                        </span>
                      }
                      style={{ paddingBottom: 9, paddingTop: 9 }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="ConsumoMinimo"
                            label="Consumo mínimo para descuento ($):"
                            name="ConsumoMinimo"
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            value={values.ConsumoMinimo}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            helperText={
                              errors.ConsumoMinimo &&
                              touched.ConsumoMinimo &&
                              errors.ConsumoMinimo
                            }
                            error={
                              errors.ConsumoMinimo && touched.ConsumoMinimo
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="HorasGratis"
                            label="Horas gratis:"
                            name="HorasGratis"
                            type="number"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={values.HorasGratis}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            helperText={
                              errors.HorasGratis &&
                              touched.HorasGratis &&
                              errors.HorasGratis
                            }
                            error={errors.HorasGratis && touched.HorasGratis}
                          />
                        </Grid>
                        <Grid container item xs={12} spacing={3} />
                      </Grid>
                    </CardContent>
                  </Card>
                  <Box display="flex" justifyContent="flex-end">
                    <CardActions>
                      <StyledButton
                        color="primary"
                        startIcon={<Save fontSize="small" />}
                        disabled={isSubmitting}
                        type="submit"
                      >
                        Guardar cambios
                      </StyledButton>
                    </CardActions>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
          <Divider style={{ marginBottom: "16px", width: "100%" }} />
          <Box mt={3}>
            <Card>
              <CardHeader
                title={
                  <span style={{ fontSize: "0.9rem" }}>
                    <AccountBalance fontSize="small" /> Cuenta bancaria
                  </span>
                }
                style={{ paddingBottom: 9, paddingTop: 9 }}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" className="m-2">
                      Seleccione una cuenta:
                    </Typography>
                    <TextField
                      fullWidth
                      name="Banco"
                      label="Banco"
                      select
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      onChange={(event) => {
                        setSelectedCuenta(
                          cuentas.find(
                            (cuenta) =>
                              cuenta.NumeroCuenta === event.target.value
                          )
                        );
                      }}
                      variant="outlined"
                      sx={{ mb: 2, mt: 1 }}
                    >
                      <option value="">Seleccione una opción</option>
                      {cuentas.map((cuenta) => (
                        <option
                          key={cuenta.NumeroCuenta}
                          value={cuenta.NumeroCuenta}
                        >
                          {cuenta.NombreCuenta}
                        </option>
                      ))}
                    </TextField>
                    <StyledButton
                      color="primary"
                      variant="contained"
                      className="m-2"
                      onClick={saveBankAccount}
                    >
                      <Save className="me-2" /> Guardar Cambio
                    </StyledButton>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <NewBank Transition={Transition} />
                    </Box>
                    <TableContainer size="small" style={{ maxHeight: "75vh" }}>
                      <Table className={classes.table}>
                        <TableHead>
                          <TableRow>
                            {columnsBanks.map((col) => (
                              <StyledCell key={col.id} align="center">
                                {col.label}
                              </StyledCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cuentas.length === 0 ? (
                            <StyledRow>
                              <StyledCell
                                align="center"
                                colSpan={columnsBanks.length}
                              >
                                <NoData />
                              </StyledCell>
                            </StyledRow>
                          ) : (
                            cuentas.map((cuenta) => (
                              <StyledRow hover key={cuenta.id}>
                                <StyledCell>{cuenta.NumeroCuenta}</StyledCell>
                                <StyledCell>{cuenta.Banco}</StyledCell>
                                <StyledCell>{cuenta.TipoCuenta}</StyledCell>
                                <StyledCell>{cuenta.Identificacion}</StyledCell>
                                <StyledCell>
                                  <EditBank
                                    cuenta={cuenta}
                                    Transition={Transition}
                                  />
                                  <DeleteBank
                                    cuenta={cuenta}
                                    Transition={Transition}
                                  />
                                </StyledCell>
                              </StyledRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
          <Divider style={{ marginBottom: "16px", width: "100%" }} />
          <Box mt={3}>
            <Card>
              <CardHeader
                title={
                  <span style={{ fontSize: "0.9rem" }}>
                    <GroupAdd fontSize="small" /> Lista de usuarios autorizados
                  </span>
                }
                style={{ paddingBottom: 9, paddingTop: 9 }}
              />
              <CardContent>
                <StyledButton
                  color="primary"
                  variant="outlined"
                  className="m-2"
                  onClick={handleCreate}
                >
                  <Add />
                  <center>Agregar</center>
                </StyledButton>
                <TableContainer size="small" style={{ maxHeight: "75vh" }}>
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        {columns.map((col) => (
                          <StyledCell key={col.id} align="center">
                            {col.label}
                          </StyledCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {authorizedResidents.map((resident) => (
                        <StyledRow hover key={resident.id}>
                          <StyledCell>{resident.nombre}</StyledCell>
                          <StyledCell>{resident.apellido}</StyledCell>
                          <StyledCell>{resident.dni}</StyledCell>
                          <StyledCell>{resident.placa}</StyledCell>
                          <StyledCell>{resident.colorPlaca}</StyledCell>
                          <StyledCell>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditClick(resident)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              onClick={() => handleDeleteClick(resident)}
                            >
                              <Delete />
                            </IconButton>
                          </StyledCell>
                        </StyledRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </>
      )}
      <Dialog
        open={openConfirmDelete}
        onClose={handleCloseConfirmDelete}
        TransitionComponent={Transition}
        maxWidth="sm"
      >
        <DialogContent>
          <Typography variant="h5" className="m-4">
            ¿Está seguro que desea eliminar este usuario?
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                disabled={loadingDelete}
                onClick={handleConfirmDelete}
              >
                Confirmar
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                color="secondary"
                variant="contained"
                onClick={handleCloseConfirmDelete}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openCreate}
        onClose={handleCloseCreate}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
      >
        <AppBar className={classes.appBar}>
          <Toolbar>
            <Typography align="center" variant="h3" className={classes.title}>
              {editingResident ? "Editar Autorizado" : "Agregar Autorizado"}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent dividers>
          <Formik
            initialValues={{
              nombre: editingResident ? editingResident.nombre : "",
              apellido: editingResident ? editingResident.apellido : "",
              dni: editingResident ? editingResident.dni : "",
              placa: editingResident ? editingResident.placa : "",
              colorPlaca: editingResident ? editingResident.colorPlaca : "",
            }}
            validationSchema={Yup.object().shape({
              nombre: Yup.string().required("Nombre requerido"),
              apellido: Yup.string().required("Apellido requerido"),
              dni: Yup.string().required("Documento de identidad requerido"),
              placa: Yup.string()
                .matches(
                  /^[A-Z]{2,4}\d{2,4}$/,
                  "Formato inválido (Ej: ABC123 o AB1234)"
                )
                .required("Placa requerida"),
              colorPlaca: Yup.string().required("Color de vehículo requerido"),
            })}
            onSubmit={handleFormSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              touched,
              values,
            }) => (
              <Form className={clsx(classes.root)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      error={errors.nombre && touched.nombre}
                      label="Nombre"
                      name="nombre"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.nombre}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        errors.nombre && touched.nombre && errors.nombre
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      error={errors.apellido && touched.apellido}
                      label="Apellido"
                      name="apellido"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.apellido}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        errors.apellido && touched.apellido && errors.apellido
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      error={errors.dni && touched.dni}
                      label="Documento de identidad"
                      name="dni"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.dni}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={errors.dni && touched.dni && errors.dni}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      error={errors.placa && touched.placa}
                      label="Placa"
                      name="placa"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.placa}
                      onChange={(e) => {
                        const upperCaseValue = e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, "");
                        handleChange({
                          target: { name: "placa", value: upperCaseValue },
                        });
                      }}
                      onBlur={handleBlur}
                      helperText={errors.placa && touched.placa && errors.placa}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      error={errors.colorPlaca && touched.colorPlaca}
                      label="Color de Vehículo"
                      name="colorPlaca"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.colorPlaca}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        errors.colorPlaca &&
                        touched.colorPlaca &&
                        errors.colorPlaca
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      <Save /> {editingResident ? "Actualizar" : "Guardar"}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigParkey;
